// Copyright 2025 Sorama B.V.
import { autoinject } from "aurelia-dependency-injection";
import { DialogController } from "aurelia-dialog";
import { ActivatedLicenseDTO, LicenseBundle } from "datatypes/SoramaWebApi";
import { CamIv64Device } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { ILicenseService } from "services/License/ILicenseService";
import { ILicenseBundleService } from "services/LicenseBundle/ILicenseBundleService";

export interface RecoverSoftwarePackagesModel {
    device?: CamIv64Device;
    missingLicenses?: ActivatedLicenseDTO[];
    missingPackages?: LicenseBundle[];
    hardwareClock?: number;
}

type DialogState = "idle" | "busy" | "success" | "error";
type MissingItem = { type: "license"; item: ActivatedLicenseDTO } | { type: "package"; item: LicenseBundle };

@autoinject
export class RecoverSoftwarePackagesDialog {
    private model: RecoverSoftwarePackagesModel = {};

    protected state: DialogState = "idle";
    protected errorMessage = "";

    // Progress tracking properties used by the view
    protected progressCurrentValue: number | null = null;
    protected progressTotalValue: number | null = null;
    protected progressPercentage: number | null = 0;
    protected progressLeftText = "Recovering packages";
    protected progressUnit = "packages";

    constructor(
        private readonly controller: DialogController,
        private readonly licenseService: ILicenseService,
        private readonly licenseBundleService: ILicenseBundleService,
        private readonly camApi: ICamApi
    ) {}

    public activate(recoverSoftwarePackagesModel: RecoverSoftwarePackagesModel) {
        // Overwrite the default properties if the property exists in the passed confirmationModel
        this.model = recoverSoftwarePackagesModel;
        this.restoreMissingLicenses();
    }

    protected cancel(): void {
        this.controller.cancel();
    }

    protected async confirm(): Promise<void> {}

    private async restoreMissingLicenses(): Promise<void> {
        const missingItems: MissingItem[] = [
            ...(this.model.missingLicenses?.map((license) => ({ type: "license" as const, item: license })) || []),
            ...(this.model.missingPackages?.map((pkg) => ({ type: "package" as const, item: pkg })) || []),
        ];

        try {
            this.state = "busy";
            this.errorMessage = "";
            this.progressCurrentValue = null;
            this.progressTotalValue = null;
            this.progressPercentage = 0;

            // Validate required data
            if (!missingItems || missingItems.length === 0) {
                this.state = "success";
                return;
            }

            if (!this.model.device?.serialNumber) {
                throw new Error("Device serial number is required for license recovery.");
            }

            if (this.model.hardwareClock === undefined || this.model.hardwareClock === null) {
                throw new Error("Hardware clock is required for license recovery.");
            }

            this.progressTotalValue = missingItems.length;

            for (let index = 0; index < missingItems.length; index++) {
                const license = missingItems[index];

                // Update progress tracking
                this.progressCurrentValue = index + 1;
                this.progressPercentage = (this.progressCurrentValue / this.progressTotalValue) * 100;

                if (license.type === "license") {
                    const encryptedLicense = await this.licenseService
                        .encryptLicense(license.item.Id, this.model.device.serialNumber, this.model.hardwareClock)
                        .catch((e) => {
                            throw new Error("Could not encrypt the license: " + e.message);
                        });

                    // Post to device
                    await this.camApi.sendLicenseToDevice(encryptedLicense);
                } else if (license.type === "package") {
                    const signedLicense = await this.licenseBundleService.getSignedLicenseBundle(
                        license.item.Id,
                        this.model.hardwareClock
                    );

                    // Send the license for the package to the device
                    await this.camApi.sendLicenseToDevice(signedLicense);
                }
            }

            this.state = "success";
        } catch (error) {
            this.state = "error";
            if (error instanceof Error) {
                this.errorMessage = error.message;
            } else {
                this.errorMessage = "An unknown error occurred while restoring licenses.";
            }
            return;
        }
    }
}
